import { createBrowserStorageAdapter, createSupabaseClient } from './index';
import { getPublicEnvVar } from '@upcaret/env-utils';

/**
 * Web-only Supabase singleton.
 *
 * IMPORTANT:
 * - This file intentionally uses `import.meta.env` (Vite/Web).
 * - Do NOT import this from React Native / Expo (Hermes can't parse `import.meta`).
 *
 * Use:
 *   import { supabase } from '@upcaret/supabase-types/client'
 */

type SupabaseClientType = ReturnType<typeof createSupabaseClient>;

const supabaseUrl = getPublicEnvVar('VITE_SUPABASE_URL');
const supabaseKey = getPublicEnvVar('VITE_SUPABASE_ANON_KEY');

function missingEnvError(): Error {
  return new Error(
    '[supabase-types] Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY in environment.',
  );
}

function createMissingEnvClient(): SupabaseClientType {
  return new Proxy(
    {},
    {
      get() {
        throw missingEnvError();
      },
    },
  ) as SupabaseClientType;
}

export const supabase: SupabaseClientType =
  supabaseUrl && supabaseKey
    ? createSupabaseClient({
        url: supabaseUrl,
        key: supabaseKey,
        auth: {
          storage: createBrowserStorageAdapter(),
          detectSessionInUrl: true,
        },
      })
    : createMissingEnvClient();

export function createFunctionUrl(functionName: string): string {
  if (!supabaseUrl) throw missingEnvError();
  return `${supabaseUrl}/functions/v1/${functionName}`;
}

export function createWebSocketFunctionUrl(functionName: string): string {
  if (!supabaseUrl) throw missingEnvError();
  // Convert HTTP(S) to WebSocket protocol
  const wsUrl = supabaseUrl
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  return `${wsUrl}/functions/v1/${functionName}`;
}

/**
 * Lazy-loaded getter for Supabase client.
 * Use this when dynamically importing to enable code-splitting:
 *
 * const { supabase } = await import('@upcaret/supabase-types/client');
 * // or
 * const mod = await import('@upcaret/supabase-types/client');
 * const client = mod.supabase;
 */
export async function getSupabaseClient() {
  return supabase;
}

export { signOutSafely } from './sign-out';
